import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { getStakingStatusLabel } from "utils/stake";
import { STAKING_STATUS } from "constants/stake";
import { AntButton } from "components/AntButton";
import { openZendeskSupportTicket } from "../../utils/zendesk";
import { Container, ContainerView } from "../../components/Container";
import { TitlePage } from "../../components/Container";
import ArrowLeftIcon from "../../components/Icons/ArrowLeftIcon";
import { useApp } from "../../contexts";
import { PolygonIcon } from "../../components/Icons/PolygonIcon";
import { AIAIcon } from "../../components/Icons/AIAIcon";
import { SGTIcon } from "../../components/Icons/SGTIcon";
import { Copy } from "../../components/Copy";
import { CopyIcon } from "../../components/Icons/CopyIcon";
import { AntSwitch } from "../../components/AntSwitch";
import { LabelValue } from "../../components/LabelValue";
import {
  getStakeTransactionDetail,
  updateAutoSubscribeStake,
} from "../../services/app";
import { calculateVotingPower, formatMoney } from "../../utils/hepler";
import moment from "moment";
import NumberFormat from "react-number-format";
import * as Notification from "../../Notification";
import {
  decimalSeparatorByCurrency,
  thousandSeparatorByCurrency,
} from "../../utils/Currency";
import { Tooltip } from "antd";
import { InfoTooltipIcon } from "../../pages/Home/styles";
import { calculateInterestAmount } from "utils/helper";
import { AutoSubscribeConfirmModal } from "../stake-sgt/components/AutoSubscribeConfirmModal";

const FORMAT_DATE = "YYYY/MM/DD";

export const StakeForOptionDetail = () => {
  const { T, getCurrency, language, user } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const [stakeData, setStakeData] = useState({});
  const [showAutoSubscribeModal, setShowAutoSubscribeModal] = useState(false);
  const [pendingAutoSubscribeValue, setPendingAutoSubscribeValue] = useState(null);

  const reStakeAmount = useMemo(() => {
    const claimableAmountNumber = Number(stakeData?.claimableAmount ?? 0);
    const claimAmountNumber = Number(stakeData?.claimAmount ?? 0);

    if (
      Number.isNaN(claimableAmountNumber) ||
      Number.isNaN(claimAmountNumber)
    ) {
      return 0;
    }

    return claimableAmountNumber - claimAmountNumber;
  }, [stakeData]);

  const isHoldingStakeTransaction = useMemo(() => {
    return stakeData.status === STAKING_STATUS.HOLDING;
  }, [stakeData]);

  const statusTooltipContentMap = useMemo(
    () => ({
      [STAKING_STATUS.PENDING]: T(`staking_status_${STAKING_STATUS.PENDING}`),
      [STAKING_STATUS.HOLDING]: T(`staking_status_${STAKING_STATUS.HOLDING}`),
      [STAKING_STATUS.FAILED]: T(`staking_status_${STAKING_STATUS.FAILED}`),
      [STAKING_STATUS.REDEEMED]: T(`staking_status_${STAKING_STATUS.REDEEMED}`),
      [STAKING_STATUS.EARLY_REDEEMED]: T(
        `staking_status_${STAKING_STATUS.EARLY_REDEEMED}`
      ),
      [STAKING_STATUS.CLAIM_IN_PROGRESS]: T(
        `staking_status_${STAKING_STATUS.CLAIM_IN_PROGRESS}`
      ),
      [STAKING_STATUS.CLAIMED]: T(`staking_status_${STAKING_STATUS.CLAIMED}`),
      [STAKING_STATUS.CLAIM_FAILED]: T(
        `staking_status_${STAKING_STATUS.CLAIM_FAILED}`
      ),
    }),
    [T]
  );

  const statusTooltipTitle = useMemo(() => {
    const normalizedStatus = String(stakeData?.status || "")
      .trim()
      .toLowerCase();

    return (
      statusTooltipContentMap[normalizedStatus] || T("Stake status information")
    );
  }, [stakeData?.status, statusTooltipContentMap, T]);

  useEffect(() => {
    const fetchStakeDetail = async () => {
      const searchParams = new URLSearchParams(location.search);
      const stakeRecordId = searchParams.get("stakeRecordId");

      if (stakeRecordId) {
        const res = await getStakeTransactionDetail(stakeRecordId);
        if (res.status === 200 && res?.data) {
          setStakeData(res.data);
        } else {
          Notification.error(T("Failed to fetch stake detail"));
          console.error("Error fetching stake detail:", res.data);
        }
      }
    };

    fetchStakeDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const onClickBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/stake-for-option");
    }
  };

  const onClickReStake = () => {
    const searchParams = new URLSearchParams();
    searchParams.set("stakeRecordId", stakeData.stakeRecordId);
    searchParams.set("txHash", stakeData.txHash);
    navigate(`/re-stake-request?${searchParams.toString()}`);
  };

  const onClickClaimOrRetryClaim = () => {
    navigate(`/claim-staking-request?stakeRecordId=${stakeData.stakeRecordId}`);
  };

  const onClickGetSupport = () => {
    openZendeskSupportTicket({
      language,
      userEmail: user.email,
      issueType: "app_issues",
      subject: `Stake for Option issue: ${stakeData.stakeRecordId}`,
      description: `- Transaction hash: ${stakeData.txHash}`,
    });
  };

  // Calculate derived values
  const rewardAmount = useMemo(() => {
    if (!stakeData) return 0;
    if (stakeData.claimableAmount === 0) {
      return calculateInterestAmount(stakeData.amount, stakeData.interestRate);
    }
    return stakeData.claimableAmount - stakeData.amount;
  }, [stakeData]);

  const votingPower = useMemo(() => {
    if (!stakeData) return 0;
    const ratio = stakeData.votingRate || 100;
    return calculateVotingPower({
      token: stakeData.amount,
      ratio: ratio,
    });
  }, [stakeData]);

  const energyEarnAmount = useMemo(() => {
    // Keep calculation aligned with StakeSGTComponent for consistency
    const energyInterestRate = stakeData?.interestRate;
    const rewardAmountNumber = Number(rewardAmount ?? 0);
    const energyInterestRateNumber = Number(energyInterestRate ?? 0);
    if (isNaN(rewardAmountNumber) || isNaN(energyInterestRateNumber)) {
      return 0;
    }
    const energyEarnAmount =
      (rewardAmountNumber * energyInterestRateNumber) / 12;
    return energyEarnAmount;
  }, [rewardAmount, stakeData]);

  const onChangeAutoSubscribe = async (checked) => {
    if (checked) {
      // Show confirmation modal when turning ON
      setPendingAutoSubscribeValue(true);
      setShowAutoSubscribeModal(true);
    } else {
      // Directly update when turning OFF
      await updateAutoSubscribeStakeAPI(false);
    }
  };

  const handleAutoSubscribeCancel = () => {
    setShowAutoSubscribeModal(false);
    setPendingAutoSubscribeValue(null);
  };

  const handleAutoSubscribeAgree = async () => {
    setShowAutoSubscribeModal(false);
    const checked = pendingAutoSubscribeValue;
    setPendingAutoSubscribeValue(null);
    await updateAutoSubscribeStakeAPI(checked);
  };

  const updateAutoSubscribeStakeAPI = async (checked) => {
    setStakeData({
      ...stakeData,
      autoSubscribeStake: checked,
    });
    const res = await updateAutoSubscribeStake({
      stakeRecordId: stakeData.stakeRecordId,
      autoSubscribe: checked ? 1 : 0,
    });
    if (res.status === 200) {
      Notification.success(T("auto_subscribe_update_success"));
    } else {
      setStakeData({
        ...stakeData,
        autoSubscribeStake: !checked,
      });
      Notification.error(T("auto_subscribe_update_fail"));
      console.error("Error updating auto subscribe stake:", res.data);
    }
  };

  const goBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/stake-for-option");
    }
  };

  return (
    <Container>
      <div className="bg-[#f5f5f5] min-h-screen">
        <ContainerView>
          <TitlePage className="flex gap-1 items-center">
            <button
              className="text-[30px] lg:text-[42px] cursor-pointer"
              onClick={goBack}
            >
              <ArrowLeftIcon />
            </button>
            {T("Stake Information")}
          </TitlePage>

          <div className="flex justify-center mt-[36px] mb-[50px]">
            <div className="bg-white rounded-[20px] p-4 md:p-[50px] w-full max-w-[784px]">
              <div className="flex flex-col gap-5 description-list">
                {/* Network */}
                <LabelValue
                  label={T("Network")}
                  value={
                    <div className="flex items-center gap-[5px]">
                      <PolygonIcon width={25} height={25} />
                      <div className="text-[16px] text-black leading-[25px]">
                        {T("Polygon Main Network")}
                      </div>
                    </div>
                  }
                />

                {/* From Address */}
                <LabelValue
                  label={T("From Address")}
                  value={
                    <div className="flex items-center gap-[5px]">
                      <AIAIcon width={24} height={22} />
                      <p className="text-[16px] text-black leading-[25px]">
                        {T("AI Avatar Web3 Wallet Address")}
                      </p>
                    </div>
                  }
                />

                {/* Interest Rate */}
                <LabelValue
                  label={T("Interest Rate")}
                  value={
                    <div className="flex items-center gap-[5px]">
                      <p className="text-[16px] text-black leading-[25px]">
                        {stakeData.interestRate}%
                      </p>
                    </div>
                  }
                />

                {/* Start date / End date */}
                <div className="flex gap-[8px] items-start">
                  <LabelValue
                    label={T("Start date")}
                    value={moment(stakeData.startTime).format(FORMAT_DATE)}
                    flex
                  />
                  <LabelValue
                    label={T("End date")}
                    value={moment(stakeData.claimTime).format(FORMAT_DATE)}
                    flex
                  />
                </div>

                {/* Stake Amount / Reward Amount */}
                <div className="flex gap-[10px] items-start">
                  <LabelValue
                    label={T("Stake Amount")}
                    value={
                      <div className="flex items-center gap-[5px]">
                        <SGTIcon width={24} height={24} />
                        <div className="text-[16px] text-black leading-[25px]">
                          <NumberFormat
                            value={stakeData.amount}
                            displayType="text"
                            thousandSeparator={thousandSeparatorByCurrency(
                              getCurrency()
                            )}
                            decimalSeparator={decimalSeparatorByCurrency(
                              getCurrency()
                            )}
                            decimalScale={3}
                            suffix=" SGT"
                          />
                        </div>
                      </div>
                    }
                    flex
                  />
                  <LabelValue
                    label={T("Reward Amount")}
                    value={
                      <div className="flex items-center gap-[5px]">
                        <SGTIcon width={24} height={24} />
                        <div className="text-[16px] text-black leading-[25px]">
                          <NumberFormat
                            value={rewardAmount}
                            displayType="text"
                            thousandSeparator={thousandSeparatorByCurrency(
                              getCurrency()
                            )}
                            decimalSeparator={decimalSeparatorByCurrency(
                              getCurrency()
                            )}
                            decimalScale={3}
                            suffix=" SGT"
                          />
                        </div>
                      </div>
                    }
                    flex
                  />
                </div>
                {/* Re-Stake Amount */}
                {[STAKING_STATUS.CLAIMED].includes(stakeData.status) && (
                  <div className="flex gap-[10px] items-start">
                    <LabelValue
                      flex
                      label={T("Re-Stake Amount")}
                      value={
                        <div className="flex items-center gap-[5px]">
                          <SGTIcon width={24} height={24} />
                          <p className="text-[16px] text-black leading-[25px]">
                            <NumberFormat
                              value={reStakeAmount}
                              displayType="text"
                              thousandSeparator={thousandSeparatorByCurrency(
                                getCurrency()
                              )}
                              decimalSeparator={decimalSeparatorByCurrency(
                                getCurrency()
                              )}
                              decimalScale={3}
                              suffix=" SGT"
                            />
                          </p>
                        </div>
                      }
                    />

                    <LabelValue
                      flex
                      label={T("Claimed Amount")}
                      value={
                        <div className="flex items-center gap-[5px]">
                          <SGTIcon width={24} height={24} />
                          <p className="text-[16px] text-black leading-[25px]">
                            <NumberFormat
                              value={stakeData.claimAmount}
                              displayType="text"
                              thousandSeparator={thousandSeparatorByCurrency(
                                getCurrency()
                              )}
                              decimalSeparator={decimalSeparatorByCurrency(
                                getCurrency()
                              )}
                              decimalScale={3}
                              suffix=" SGT"
                            />
                          </p>
                        </div>
                      }
                    />
                  </div>
                )}

                {/* Claimable Amount */}
                {[
                  STAKING_STATUS.REDEEMED,
                  STAKING_STATUS.CLAIM_FAILED,
                  STAKING_STATUS.CLAIM_IN_PROGRESS,
                ].includes(stakeData.status) && (
                  <LabelValue
                    label={T("Claimable Amount")}
                    value={
                      <div className="flex items-center gap-[8px]">
                        <SGTIcon width={24} height={24} />
                        <p className="text-[16px] text-black leading-[25px]">
                          <NumberFormat
                            value={stakeData.claimableAmount || 0}
                            displayType="text"
                            thousandSeparator={thousandSeparatorByCurrency(
                              getCurrency()
                            )}
                            decimalSeparator={decimalSeparatorByCurrency(
                              getCurrency()
                            )}
                            decimalScale={3}
                            suffix=" SGT"
                          />
                        </p>
                      </div>
                    }
                  />
                )}

                {/* Voting power */}
                <LabelValue
                  label={T("Voting Power")}
                  value={formatMoney(votingPower, 0)}
                />

                {/* Est. Monthly Energy Earn */}
                <LabelValue
                  label={T("Est. Monthly Energy Earn")}
                  value={`${formatMoney(energyEarnAmount, 2)} e`}
                />

                {/* Transaction Hash */}
                <LabelValue
                  label={T("Stake Transaction Hash")}
                  labelClassName="leading-[24px]"
                  value={
                    <div className="flex items-center">
                      {stakeData.txHash ? (
                        <Copy
                          textStyle={{ fontWeight: 400 }}
                          className="justify-start gap-3"
                          cutText={true}
                          splitFont={16}
                          splitFontLg={16}
                          numDot={3}
                          text={stakeData.txHash}
                          icon={<CopyIcon />}
                        />
                      ) : (
                        <span className="text-[16px] text-black">
                          {T("N/A")}
                        </span>
                      )}
                    </div>
                  }
                />

                {/* Status */}
                <LabelValue
                  label={T("Status")}
                  labelClassName="leading-[24px]"
                  value={
                    <div className="flex items-center gap-[20px]">
                      <span>{getStakingStatusLabel(stakeData.status)}</span>
                      <Tooltip trigger="hover" title={statusTooltipTitle}>
                        <div className="cursor-pointer">
                          <InfoTooltipIcon className="w-[15px] h-[15px]" />
                        </div>
                      </Tooltip>
                    </div>
                  }
                />

                {/* Auto subscribe */}
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-2">
                    <p className="text-base font-bold text-[#818181]">
                      {T("Auto subscribe")}
                    </p>
                    <div className="flex-1 flex justify-end">
                      <AntSwitch
                        disabled={!isHoldingStakeTransaction}
                        checked={!!stakeData.autoSubscribeStake || false}
                        onChange={onChangeAutoSubscribe}
                        checkedChildren="ON"
                        unCheckedChildren="OFF"
                      />
                    </div>
                  </div>
                  <p className="text-base text-black">
                    {T("auto_subscribe_description")}
                  </p>
                </div>
              </div>

              {/* Buttons */}
              <div className="flex justify-center mt-5 md:mt-[50px] gap-2 md:gap-3">
                {[
                  STAKING_STATUS.HOLDING,
                  STAKING_STATUS.CLAIM_IN_PROGRESS,
                  STAKING_STATUS.CLAIMED,
                ].includes(stakeData.status) && (
                  <AntButton
                    variant="primary"
                    onClick={onClickBack}
                    className="flex-1 md:flex-none"
                  >
                    {T("Back")}
                  </AntButton>
                )}
                {stakeData.status === STAKING_STATUS.REDEEMED && (
                  <>
                    <AntButton
                      variant="outline"
                      onClick={onClickClaimOrRetryClaim}
                      className="flex-1 md:flex-none min-w-[unset] md:min-w-[175px]"
                    >
                      {T("Claim")}
                    </AntButton>
                    <AntButton
                      variant="primary"
                      onClick={onClickReStake}
                      className="flex-1 md:flex-none min-w-[unset] md:min-w-[175px]"
                    >
                      {T("Re-stake")}
                    </AntButton>
                  </>
                )}
                {stakeData.status === STAKING_STATUS.CLAIM_FAILED && (
                  <>
                    {!(stakeData?.claimAttempts >= 2) && (
                      <AntButton
                        variant="outline"
                        onClick={onClickClaimOrRetryClaim}
                        className="flex-1 md:flex-none min-w-[unset] md:min-w-[175px]"
                      >
                        {T("Retry Claim")}
                      </AntButton>
                    )}
                    <AntButton
                      variant="primary"
                      onClick={onClickGetSupport}
                      className="flex-1 md:flex-none min-w-[unset] md:min-w-[175px]"
                    >
                      {T("Get Support")}
                    </AntButton>
                  </>
                )}
              </div>
            </div>
          </div>
        </ContainerView>
      </div>
      <AutoSubscribeConfirmModal
        open={showAutoSubscribeModal}
        onCancel={handleAutoSubscribeCancel}
        onAgree={handleAutoSubscribeAgree}
      />
    </Container>
  );
};
